import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { Alert } from "../Alert/Alert.js";
import { useOptionalBrand } from "../ThemeRoot/BrandContext.js";
import { brandAccentStyle, deriveAccentRamp } from "../../lib/accent.js";
import { Input } from "../Input/Input.js";
/* empty css                      */
import { useId, useMemo, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/ColorSwatchPicker/ColorSwatchPicker.tsx
/** The prototype's accent presets. */
var DEFAULT_ACCENT_PRESETS = [
	{
		value: "#2563eb",
		name: "Blue"
	},
	{
		value: "#4f46e5",
		name: "Indigo"
	},
	{
		value: "#16a34a",
		name: "Green"
	},
	{
		value: "#334155",
		name: "Slate"
	}
];
var HEX = /^#[0-9a-f]{6}$/i;
/** The accent's contrast result, or null when it isn't a valid #rrggbb colour. */
function checkAccent(value, surfaces) {
	if (!value || !HEX.test(value)) return null;
	return brandAccentStyle(value, { surfaces }).result;
}
/**
* Picks a brand accent (white-label Settings): a radio group of swatches, and optionally
* a custom hex. An accent that fails contrast is adjusted when applied; the picker warns
* with the original and adjusted colours.
*/
function ColorSwatchPicker({ value, onChange, presets = DEFAULT_ACCENT_PRESETS, allowCustom = false, label = "Accent colour", disabled = false, surfaces: surfacesProp, className, ...rest }) {
	const id = useId();
	const brand = useOptionalBrand();
	const surfaces = surfacesProp ?? brand?.pack.surfaces;
	const normalised = value?.toLowerCase() ?? null;
	const isPreset = presets.some((preset) => preset.value.toLowerCase() === normalised);
	const [customChosen, setCustomChosen] = useState(!isPreset && normalised !== null);
	const custom = allowCustom && (customChosen || !isPreset && normalised !== null);
	const [draft, setDraft] = useState(custom && value ? value : "");
	const result = useMemo(() => checkAccent(value, surfaces), [value, surfaces]);
	const choosePreset = (preset) => {
		setCustomChosen(false);
		onChange(preset.value);
	};
	const chooseCustom = () => {
		setCustomChosen(true);
		if (HEX.test(draft)) onChange(draft.toLowerCase());
	};
	const typeCustom = (text) => {
		const next = text.startsWith("#") || text === "" ? text : `#${text}`;
		setDraft(next);
		if (HEX.test(next)) onChange(next.toLowerCase());
	};
	return /* @__PURE__ */ jsxs("fieldset", {
		...rest,
		disabled,
		className: cx("ds-swatch-picker", disabled && "ds-swatch-picker--disabled", className),
		children: [
			/* @__PURE__ */ jsx("legend", {
				className: "ds-swatch-picker__legend",
				children: label
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "ds-swatch-picker__swatches",
				children: [presets.map((preset) => {
					const checked = !custom && preset.value.toLowerCase() === normalised;
					return /* @__PURE__ */ jsx(Swatch, {
						name: `${id}accent`,
						colour: preset.value,
						label: `${preset.name} ${preset.value.toLowerCase()}`,
						checked,
						onSelect: () => choosePreset(preset)
					}, preset.value);
				}), allowCustom && /* @__PURE__ */ jsxs("label", {
					className: cx("ds-swatch-picker__custom", custom && "ds-swatch-picker__custom--checked"),
					children: [/* @__PURE__ */ jsx("input", {
						type: "radio",
						name: `${id}accent`,
						className: "ds-swatch-picker__radio",
						checked: custom,
						onChange: chooseCustom
					}), /* @__PURE__ */ jsx("span", {
						className: "ds-swatch-picker__custom-label",
						children: "Custom"
					})]
				})]
			}),
			custom && /* @__PURE__ */ jsx("div", {
				className: "ds-swatch-picker__hex",
				children: /* @__PURE__ */ jsx(Input, {
					"aria-label": "Custom accent, hex",
					size: "sm",
					value: draft,
					placeholder: "#2563eb",
					maxLength: 7,
					spellCheck: false,
					autoComplete: "off",
					invalid: draft !== "" && !HEX.test(draft),
					onChange: (event) => typeCustom(event.target.value),
					leading: /* @__PURE__ */ jsx("span", {
						className: "ds-swatch-picker__preview",
						style: { background: HEX.test(draft) ? draft : void 0 },
						"aria-hidden": "true"
					})
				})
			}),
			/* @__PURE__ */ jsx(ContrastWarning, { result })
		]
	});
}
function Swatch({ name, colour, label, checked, onSelect }) {
	const checkColour = useMemo(() => {
		try {
			return deriveAccentRamp(colour, { mode: "light" }).ramp.primaryFg;
		} catch {
			return;
		}
	}, [colour]);
	return /* @__PURE__ */ jsxs("label", {
		className: "ds-swatch-picker__swatch",
		style: { background: colour },
		children: [/* @__PURE__ */ jsx("input", {
			type: "radio",
			name,
			className: "ds-swatch-picker__radio",
			"aria-label": label,
			checked,
			onChange: onSelect
		}), checked && /* @__PURE__ */ jsx("span", {
			className: "ds-swatch-picker__check",
			style: { color: checkColour },
			children: /* @__PURE__ */ jsx(Icon, { name: "check" })
		})]
	});
}
/**
* Says when the accent is adjusted to meet contrast, with the original and adjusted hex.
* Only light mode counts: dark mode always lightens a mid-tone accent (three of the four
* presets), which is expected, not a problem with the tenant's choice.
*/
function ContrastWarning({ result }) {
	const adjusted = result ? result.used.light !== result.original : false;
	return /* @__PURE__ */ jsx("div", {
		className: "ds-swatch-picker__warning",
		"aria-live": "polite",
		children: adjusted && result && /* @__PURE__ */ jsxs(Alert, {
			tone: "warning",
			title: "Adjusted for contrast",
			children: [
				result.original,
				" is too hard to read against the page, so it shows as ",
				result.used.light,
				" ",
				"in light mode",
				result.used.dark !== result.original ? ` and ${result.used.dark} in dark mode` : "",
				"."
			]
		})
	});
}
//#endregion
export { ColorSwatchPicker, DEFAULT_ACCENT_PRESETS };

//# sourceMappingURL=ColorSwatchPicker.js.map